import { Box, Container, Grid, Typography, Button } from "@mui/material";
import LuxuryInput from "./LuxuryInput";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import { useMutation } from '@tanstack/react-query';
import { contactForm } from "../../api/services";

const schema: yup.ObjectSchema<Omit<LeadData, 'type'>> = yup.object({
  name: yup.string().required('Name is required'),
  email: yup
    .string()
    .email('Invalid email format')
    .required('Email is required'),
  phoneNumber: yup
    .string()
    .matches(
      /^[6-9]\d{9}$/,
      'Phone number must start with 6, 7, 8, or 9 and be 10 digits',
    )
    .required('Phone number is required'),
  message: yup.string().optional(),
  source: yup.string().optional(),
});

// ✅ UPDATED: Added source field to interface
interface LeadData {
  name: string;
  email: string;
  phoneNumber: string;
  message?: string;
  type?: string;
  source?: string;
}

const ContactSection = () => {
   // ✅ ALWAYS call hooks FIRST
    const {
      control,
      handleSubmit,
      reset,
      formState: { errors },
    } = useForm<LeadData>({
      resolver: yupResolver(schema),
    });
  
    const mutation = useMutation({
      mutationFn: (newFormData: LeadData) => contactForm(newFormData),
      onSuccess: () => {
        toast.success('Successfully submitted!');
        resetForm();
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || 'Submission failed');
      },
    });
  
    const resetForm = () => {
      reset({
        name: '',
        email: '',
        phoneNumber: '',
        message: '',
      });
    };
  
    const onSubmit = (data: LeadData) => {
      mutation.mutate({ ...data, type: 'contact', source: 'Mandate webpage' });
    };
  
  return (
    <Box
      sx={{
        py: { xs: 10, md: 4 },
        background: "#f4f8fc",
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={8} alignItems="center">

          {/* LEFT */}
          <Grid item xs={12} md={5} textAlign='start'>
            <Typography
              variant="h4"
              sx={{

                fontSize: { xs: 32, md: 42 },
                color: "#0b1f33",
                mb: 3,
                fontWeight: 600,
              }}
            >
              Get in Touch
            </Typography>

            <Typography sx={{ color: "#5b6b7c" }}>
              Ready to bring structure, clarity, and control to your sales? Let’s start the conversation.

            </Typography>
            <Box sx={{ mb: 3, mt: 2 ,textAlign:'start'}}>
              <Typography
                sx={{
                  color: "#000",
                  fontSize: 18,
                  mb: 1,
                  fontFamily: "Inter",
                }}
              >
                Email
              </Typography>

              <Typography
                component="a"
                href="mailto:connect@rajivwilliams.com"
                sx={{
                  color: "#0f63a5",
                  fontSize: 15,
                   fontFamily: '"Playfair Display", serif !important',
                   textDecoration: "none",
                }}
              >
                connect@rajivwilliams.com
              </Typography>
            </Box>

            <Box>
              <Typography
                sx={{
                  color: "#000",
                  fontSize: 15,
                  mb: 1,
                  fontFamily: "Inter",
                }}
              >
                Phone 
              </Typography>

              <Typography
               component="a"
                href="tel:+919885420885" 
                sx={{
                  color: "#0f63a5",
                fontSize: 15,
                
                textDecoration: "none",
                 fontFamily: 'Poppins, sans-serif !important',
                "&:hover": {
                  textDecoration: "underline",
                },
                }}
              >
                +91 98854 20885
              </Typography>
            </Box>
          </Grid>

          {/* 💎 RIGHT FORM */}
          <Grid item xs={12} md={7}>
            <Box
              sx={{
                p: 6,
                borderRadius: "32px",
                position: "relative",
                overflow: "hidden",

                // 🔵 LUXURY BLUE GLASS CARD
                background: `
                  linear-gradient(
                    145deg,
                    #0f63a5,
                    #0c4f85
                  )
                `,

                boxShadow: `
                  0 30px 80px rgba(15,99,165,0.4),
                  0 10px 30px rgba(0,0,0,0.25)
                `,
              }}
            >
              {/* 🔥 GLASS BLUR LAYER */}
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  backdropFilter: "blur(20px)",
                  background: "rgba(255,255,255,0.05)",
                }}
              />

              {/* 🔥 GLOSS LIGHT */}
              <Box
                sx={{
                  position: "absolute",
                  top: "-40%",
                  left: "-20%",
                  width: "160%",
                  height: "160%",
                  background:
                    "radial-gradient(circle, rgba(255,255,255,0.3), transparent 60%)",
                }}
              />

              <Box 
                 onSubmit={handleSubmit(onSubmit)}
          component="form"
              sx={{
                 position: "relative", zIndex: 2,textAlign:"start" 
                 }}>

                {/* 💎 INPUTS */}
                <Controller
                              name="name"
                              control={control}
                              render={({ field }) => (
                                <LuxuryInput
                                  placeholder="Enter your full name"
                                  {...field}
                                  fullWidth
                
                                  error={!!errors.name}
                                  helperText={errors.name?.message}
                                />
                              )}
                            />
                <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <LuxuryInput
                  placeholder="Enter your email address"
                  {...field}
                  fullWidth

                  error={!!errors.email}
                  helperText={errors.email?.message}
                />
              )}
            />
            <Controller
                          name="phoneNumber"
                          control={control}
                          render={({ field }) => (
                            <LuxuryInput
                              placeholder="Enter your phone number"
                              {...field}
                              fullWidth  
                              error={!!errors.phoneNumber}
                              helperText={errors.phoneNumber?.message}
                            />
                          )}
                        />
                        <Controller
              name="message"
              control={control}
              render={({ field }) => (
                <LuxuryInput
                  placeholder="Enter your message"
                  {...field}
                  multiline
                  rows={3}
                  fullWidth

                />
              )}
            />
      
                {/* 💎 BUTTON */}
                <Button
                  fullWidth
                    type="submit"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    mt: 2,
                    py: 1.6,
                    borderRadius: "40px",
                    fontWeight: 600,
                    textTransform: "none",

                    background: "#ffffff",
                    color: "#0f63a5",

                    boxShadow: "0 15px 40px rgba(0,0,0,0.25)",

                    "&:hover": {
                      background: "#eaf3ff",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  Send Message
                </Button>
              </Box>
            </Box>
          </Grid>

        </Grid>
        {/* FOOTER */}
        {/* <Box
          sx={{
            borderTop: "1px solid rgba(25, 105, 196, 0.66)",
            mt: 10,
            pt: 4,
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              color: "#64748b",
              fontSize: 14,
            }}
          >
            © 2026 Rajiv Williams. All rights reserved. | Sales & Marketing
            Mandate for Real Estate Developers
          </Typography>
        </Box> */}
      </Container>
    </Box>
  );
};

export default ContactSection;